import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AsyncReduxState, initialAsyncReduxState } from '../../../redux/@config/AsyncReduxState'
import { Department } from '../../entities/Department/Department'

export type DepartmentsState = AsyncReduxState<{
    departments: Department[];
    loadingDepartmentIds: string[];
}>;

const initialState: DepartmentsState = {
    ...initialAsyncReduxState,
    departments: [],
    loadingDepartmentIds: [],
}

export const DepartmentsSlice = createSlice({
    name: 'departmentGroupsReducer',
    initialState,
    reducers: {
        setIsLoading(state, action: PayloadAction<boolean>): DepartmentsState {
            return {
                ...state,
                isLoading: action.payload,
            }
        },
        setDepartments(state, action: PayloadAction<Department[]>): DepartmentsState {
            return {
                ...state,
                departments: action.payload,
            }
        },
        setError(state, action: PayloadAction<string>): DepartmentsState {
            return {
                ...state,
                error: action.payload,
            }
        },
        departmentAdded(state, action: PayloadAction<Department>): DepartmentsState {
            if (state.departments.some(department => department.id === action.payload.id)) {
                throw new Error('Department already exists')
            }

            return {
                ...state,
                departments: [...state.departments, action.payload],
            }
        },
        departmentPatched(state, action: PayloadAction<Partial<Department>>): DepartmentsState {
            const departmentToUpdate = state.departments.find(department => department.id === action.payload.id)

            if (! departmentToUpdate) {
                throw new Error('Department not found')
            }

            const updatedDepartment = { ...departmentToUpdate, ...action.payload }

            return {
                ...state,
                departments: state.departments.map(department => department.id === updatedDepartment.id ? updatedDepartment : department),
            }
        },
        departmentDeleted(state, action: PayloadAction<string>): DepartmentsState {
            return {
                ...state,
                departments: state.departments.filter(department => department.id !== action.payload),
            }
        },
        setLoadingDepartment(state, action: PayloadAction<string>): DepartmentsState {
            return {
                ...state,
                loadingDepartmentIds: [...state.loadingDepartmentIds, action.payload],
            }
        },
        removeLoadingDepartment(state, action: PayloadAction<string>): DepartmentsState {
            return {
                ...state,
                loadingDepartmentIds: state.loadingDepartmentIds.filter(departmentId => departmentId !== action.payload),
            }
        },
    },
})

export const {
    setIsLoading,
    setDepartments,
    departmentAdded,
    departmentPatched,
    departmentDeleted,
    setLoadingDepartment,
    removeLoadingDepartment,
    setError,
} = DepartmentsSlice.actions

export default DepartmentsSlice.reducer
